import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { claimPhotographyBrief } from '__generated__/claimPhotographyBrief';
import {
    getPhotographyBrief,
    getPhotographyBrief_structuredBrief,
    getPhotographyBriefVariables,
} from '__generated__/getPhotographyBrief';
import { BriefState } from '__generated__/types';
import {
    updatePhotographyBrief,
    updatePhotographyBrief_updateStructuredBrief_errors as PhotographyValidationError,
    updatePhotographyBrief_updateStructuredBrief_fields_SmartShootPhotographyFields as PhotographyBriefFields,
    updatePhotographyBriefVariables,
} from '__generated__/updatePhotographyBrief';
import { handleGraphQLError } from 'common/utils/Apollo';
import { AuthConsumer } from 'projects/components/AuthContext';
import ContactForm from 'projects/components/SmartShoot/ContactForm';
import PhotographyProjectDetailsForm from 'projects/components/SmartShoot/PhotographyProjectDetailsForm';
import PhotographyShootForm from 'projects/components/SmartShoot/PhotographyShootForm';
import { SmartShootBrief } from 'projects/components/SmartShoot/SmartShootBrief';
import { trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import queries from 'projects/routes/SmartShootPhotographyBrief.graphql';
import React from 'react';
import { ApolloConsumer, Query } from 'react-apollo';
import { redirectToSmartShootGetStarted } from '.';

class SmartShootPhotographyBrief extends SmartShootBrief<
    PhotographyBriefFields,
    getPhotographyBrief_structuredBrief,
    PhotographyValidationError
> {}

interface WrappedBriefProps {
    briefId: string;
}

const WrappedBrief: React.FunctionComponent<WrappedBriefProps> = props => (
    <ApolloConsumer>
        {query => {
            return (
                <Query<getPhotographyBrief, getPhotographyBriefVariables>
                    query={queries.getPhotographyBrief}
                    variables={{ id: props.briefId }}
                >
                    {({ data, error, loading }) => {
                        if (loading) {
                            return <LoadableElement isLoading={true} variations={['relative ']} />;
                        }

                        if (error) {
                            handleGraphQLError(error);
                        }

                        if (data && data.structuredBrief && data.structuredBrief.fields) {
                            if (data.structuredBrief.state === BriefState.LAUNCHED) {
                                redirectToSmartShootGetStarted(
                                    data.structuredBrief.foreignKeys[0].toString()
                                );
                            }

                            return (
                                <AuthConsumer>
                                    {({ login }) => (
                                        <SmartShootPhotographyBrief
                                            client={query}
                                            brief={data.structuredBrief}
                                            login={login}
                                            viewer={data.viewer}
                                            industries={data.industries}
                                            documentTitle="Tell us about your photography project | 99designs"
                                            heroHeading="Tell us about your photography project"
                                            heroParagraph="Complete this short creative brief so we can match you with the perfect photographer for the job."
                                            briefType="photo"
                                            paymentRoute={routes.photographyBriefPayment(
                                                data.structuredBrief.id
                                            )}
                                            trackViewedEvent={() =>
                                                trackViewedBriefEvent({
                                                    id: data.structuredBrief.id,
                                                    page: 'Photography',
                                                })
                                            }
                                            onSave={changes => {
                                                return query
                                                    .mutate<
                                                        updatePhotographyBrief,
                                                        updatePhotographyBriefVariables
                                                    >({
                                                        mutation: queries.updatePhotographyBrief,
                                                        variables: {
                                                            id: data.structuredBrief.id,
                                                            fields: {
                                                                smartShootPhotography: Object.assign(
                                                                    {},
                                                                    changes
                                                                ),
                                                            },
                                                        },
                                                    })
                                                    .then(res => {
                                                        return res
                                                            ? res.data!.updateStructuredBrief.errors
                                                            : [];
                                                    });
                                            }}
                                            onClaim={() => {
                                                return query.mutate<claimPhotographyBrief>({
                                                    mutation: queries.claimPhotographyBrief,
                                                    variables: {
                                                        id: data.structuredBrief.id,
                                                    },
                                                });
                                            }}
                                        >
                                            {form => {
                                                return (
                                                    <>
                                                        <PhotographyProjectDetailsForm
                                                            step={1}
                                                            form={form}
                                                        />
                                                        <PhotographyShootForm
                                                            step={2}
                                                            form={form}
                                                        />
                                                        <ContactForm
                                                            step={3}
                                                            form={form}
                                                            industries={data.industries}
                                                            briefType="photo"
                                                        />
                                                    </>
                                                );
                                            }}
                                        </SmartShootPhotographyBrief>
                                    )}
                                </AuthConsumer>
                            );
                        }
                        return null;
                    }}
                </Query>
            );
        }}
    </ApolloConsumer>
);

export default WrappedBrief;
